import React, { FC } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemState,
} from 'react-accessible-accordion';
import AnimateHeight from 'react-animate-height';
import { FormattedMessage } from 'react-intl';
import { ResultsType } from '../../../../state/reducers/results.types';
import styles from './legal_basis_accordion.module.scss';
import { doctrineAnalytics } from '../../../../../utils/analyticsUtils';
import { HyperlinkRestrictedBaseModal } from '../../../../../components/HyperlinkRestrictedBaseModal';
import { MaterialIcon } from '../../../../../components/material-icon/MaterialIcon';
import { hasQueryParams } from '../../../../../utils/url-utils/urlUtils';
import { ArticleStats } from '../../../../../components/article-stats/ArticleStats';

type Legislation = NonNullable<ResultsType['snippets']['legalBasis']>[0];
type Articles = Legislation['articles'];

type Props = {
  legislation: Legislation;
  isFirst: boolean;
  searchQueryUrl?: string;
  eventName: string;
  source: string;
  ficheTitle?: string;
};

export const LegalBasisSnippetAccordion: FC<Props> = ({
  legislation,
  eventName,
  source,
  searchQueryUrl = '',
  ficheTitle = '',
  isFirst,
}) => {
  const { mainTitle, articles } = legislation;

  const reportClick = (
    expanded: boolean | undefined,
    article: Articles[0],
  ) => () => {
    doctrineAnalytics(eventName, {
      ...(ficheTitle ? { title: ficheTitle } : {}),
      source,
      reportOriginalQueryKey: true,
      article_id: article.docId,
      parent_text_short_title: mainTitle,
      title: article.title,
      expand_action: expanded ? 'close' : 'open',
      // parent_text_nature, // TODO data missing from service
    });
  };

  const buildUrl = (url: string, searchQuery: string) => {
    if (!searchQuery) return url;
    if (hasQueryParams(url)) return `${url}&${searchQuery}`;
    return `${url}?${searchQueryUrl}`;
  };

  const preExpandFistItemOfFirstAccordion = isFirst ? ['0'] : [];
  return (
    <Accordion
      allowZeroExpanded
      allowMultipleExpanded
      className={styles.container}
      preExpanded={preExpandFistItemOfFirstAccordion}
    >
      {articles.map((article, index) => (
        <AccordionItem
          key={article.docId}
          uuid={`${index}`}
          className={styles.itemContainer}
        >
          <AccordionItemHeading>
            <AccordionItemButton>
              <AccordionItemState>
                {({ expanded }) => (
                  <div
                    className={styles.item}
                    onClick={reportClick(expanded, article)}
                  >
                    <span className={styles.label}>{article.title}</span>
                    <MaterialIcon
                      className={styles.icon}
                      name={
                        expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }
                    />
                  </div>
                )}
              </AccordionItemState>
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemState>
            {({ expanded }) => (
              <AnimateHeight
                duration={200}
                height={expanded ? 'auto' : 0}
                easing={expanded ? 'ease-out' : 'ease-in'}
              >
                <div className={styles.articleContainer}>
                  <p
                    className={styles.article}
                    dangerouslySetInnerHTML={{
                      __html: article.content || article.contentHtml || '',
                    }}
                  />
                  <HyperlinkRestrictedBaseModal
                    source={source}
                    url={buildUrl(article.url, searchQueryUrl)}
                    className={styles.link}
                  >
                    <FormattedMessage id="search/components/search-snippets/aside/legal-basis/LegalBasisSnippetAccordion.tsx/1" />
                  </HyperlinkRestrictedBaseModal>
                  <ArticleStats
                    articleId={article.docId}
                    url={article.url}
                    sourcePage={source}
                  />
                </div>
              </AnimateHeight>
            )}
          </AccordionItemState>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
